import {
    Box,
    CardContent,
    Divider,
    makeStyles,
    Typography,
} from "@material-ui/core";
import {
    KeyboardArrowLeft,
    KeyboardArrowRight,
    ThumbDown,
    ThumbUp,
} from "@material-ui/icons";
import Cookies from "js-cookie";
import clsx from "clsx";
import React from "react";
import config from "../../../config/apiConfig";
import StarRating from "../../StarRating/StartRating";

type Payload = {
    story_overall: number;
    story_unique: number;
    conflict_interesting: number;
    story_relevant: number;
    story_engaging: number;
    plot_predictable: number;
    review_story_like: string;
    review_story_dislike: string;
    review_protagonist: string;
    review_story_characters: string;
};

type Review = {
    id: string;
    storyOverall: number;
    userId: number;
    userName: string;
    writerRating: number;
    payLoad: Payload;
};

const useStyles = makeStyles((theme) => ({
    flex: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
    circle: {
        padding: theme.spacing(0.25),
        marginRight: theme.spacing(1),
        borderRadius: "50%",
        backgroundColor: theme.palette.grey[200],
        border: "none",
    },
    ratingBox: {
        border: "1.5px solid #eee",
        padding: theme.spacing(2),
        marginBottom: theme.spacing(1),
        borderRadius: 8,
    },
    rating: {
        fontSize: 14,
        fontWeight: "bold",
        color: "#575757",
    },
    danger: {
        color: "#ad170a",
        fill: "#ad170a",
    },
    success: {
        color: "#159d57",
        fill: "#159d57",
    },
}));

const requestParams = {
    method: "POST",
    body: null,
    headers: {
        "Access-Control-Allow-Origin": "*",
        session_token: Cookies.get("token") ?? "",
        token: config.token,
    },
};

const CriticSection: React.FC<{ id: string }> = ({ id }) => {
    const [reviews, setReviews] = React.useState<Review[]>([]);
    const [index, setIndex] = React.useState<number>(0);

    const classes = useStyles();

    React.useEffect(() => {
        fetch(
            `${config.BASE_URL}/v2/api/review/getCriticReview?storyId=${id}&count=10`,
            requestParams
        )
            .then((res) => res?.json())
            .then((res) => setReviews(res || []));
    }, []);

    const setRating = (index: number, rating: number) => {
        const oldRating = reviews[index].writerRating;
        const reviewId = reviews[index].id;
        const newReviews = [...reviews];
        newReviews[index].writerRating = rating;
        setReviews(newReviews);

        fetch(
            config.BASE_URL +
                "/v2/api/writer/rateCritic?id=" +
                reviewId +
                "&rating=" +
                rating,
            requestParams
        )
            .then((res) => res?.json())
            .then((res) => {
                if (!res) {
                    newReviews[index].writerRating = oldRating;
                    setReviews(newReviews);
                }
            })
            .catch((err) => {
                newReviews[index].writerRating = oldRating;
                setReviews(newReviews);
            });
    };

    const getRemark = (rating: number) => {
        if (rating < 3.3) {
            return (
                <div className={classes.rating}>
                    <ThumbDown className={classes.danger} />{" "}
                    <span className={classes.danger}>Poor</span>
                </div>
            );
        } else if (rating < 6.6) {
            return <div className={classes.rating}>Just ok</div>;
        } else {
            return (
                <div className={classes.rating}>
                    <ThumbUp className={classes.success} />{" "}
                    <span className={classes.success}>Excellent</span>
                </div>
            );
        }
    };

    return (
        <React.Fragment>
            <CardContent className={classes.flex}>
                <Typography variant="subtitle1" color="textSecondary">
                    Showing {index + 1} of {reviews.length} feedbacks from
                    critics
                </Typography>
                <div className={classes.flex}>
                    <button
                        disabled={index === 0}
                        className={classes.circle}
                        onClick={() => setIndex((s) => s - 1)}
                    >
                        <KeyboardArrowLeft />
                    </button>
                    <button
                        disabled={index === reviews.length - 1}
                        className={classes.circle}
                        onClick={() => setIndex((s) => s + 1)}
                    >
                        <KeyboardArrowRight />
                    </button>
                </div>
            </CardContent>
            <Divider />
            <CardContent>
                <Box className={classes.flex} marginBottom={1}>
                    <Typography variant="subtitle1">
                        <strong>{reviews[index]?.userName}</strong>
                    </Typography>
                </Box>
                <Box className={clsx(classes.flex, classes.ratingBox)}>
                    <Typography variant="body2">
                        How interesting is the problem/conflict?
                    </Typography>
                    {getRemark(reviews[index]?.payLoad?.conflict_interesting)}
                </Box>
                <Box className={clsx(classes.flex, classes.ratingBox)}>
                    <Typography variant="body2">
                        How unique is the story?
                    </Typography>
                    {getRemark(reviews[index]?.payLoad?.story_unique)}
                </Box>
                <Box className={clsx(classes.flex, classes.ratingBox)}>
                    <Typography variant="body2">
                        How relevant is the story?
                    </Typography>
                    {getRemark(reviews[index]?.payLoad?.story_relevant)}
                </Box>
                <Box className={clsx(classes.flex, classes.ratingBox)}>
                    <Typography variant="body2">
                        How engaging is the story?
                    </Typography>
                    {getRemark(reviews[index]?.payLoad?.story_engaging)}
                </Box>
                <Box className={clsx(classes.flex, classes.ratingBox)}>
                    <Typography variant="body2">
                        How much is the plot predictable?
                    </Typography>
                    {getRemark(reviews[index]?.payLoad?.plot_predictable)}
                </Box>
                <Box my={2}>
                    <Typography variant="body1">
                        What did you like about the story?
                    </Typography>
                    <Divider style={{ margin: "8px 0" }} />
                    <Typography variant="body2" color="textSecondary">
                        {reviews[index]?.payLoad?.review_story_like}
                    </Typography>
                </Box>
                <Box my={2}>
                    <Typography variant="body1">
                        What did you not like about the story?
                    </Typography>
                    <Divider style={{ margin: "8px 0" }} />
                    <Typography variant="body2" color="textSecondary">
                        {reviews[index]?.payLoad?.review_story_dislike}
                    </Typography>
                </Box>
                <Box my={2}>
                    <Typography variant="body1">
                        Which characters in the story stay with you and why?
                    </Typography>
                    <Divider style={{ margin: "8px 0" }} />
                    <Typography variant="body2" color="textSecondary">
                        {reviews[index]?.payLoad?.review_story_characters}
                    </Typography>
                </Box>
                <Box my={2}>
                    <Typography variant="body1">
                        Do you care what happens to the protagonist and why?
                    </Typography>
                    <Divider style={{ margin: "8px 0" }} />
                    <Typography variant="body2" color="textSecondary">
                        {reviews[index]?.payLoad?.review_protagonist}
                    </Typography>
                </Box>
                <Box
                    className={clsx(classes.ratingBox, classes.flex)}
                    style={{ flexFlow: "column" }}
                >
                    <Typography variant="body1" style={{fontWeight: "bold"}}>
                        How useful did you find the review?
                    </Typography>
                    <StarRating
                        rating={reviews[index]?.writerRating || 0}
                        setRating={(rating) => setRating(index, rating)}
                    />
                    <Typography variant="body2" color="textSecondary">
                        What is this rating?
                    </Typography>
                </Box>
            </CardContent>
        </React.Fragment>
    );
};

export default CriticSection;
